import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { Imovel } from "../scripts/db";

interface PropertyCardProps {
  property: Imovel;
}

function toNumber(v: number | null | undefined): number {
  return v ?? 0;
}

function formatCurrency(v: number): string {
  return v.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
}

const TIPO_DISPLAY: Record<string, string> = {
  casa: "Casa",
  apto: "Apartamento",
  loja: "Loja",
  lote: "Lote",
  sala: "Sala",
  outros: "Outros",
};

const PropertyCard: React.FC<PropertyCardProps> = ({ property }) => {
  const navigate = useNavigate();
  const imageCount = Math.min(property.images.length, 8);
  const [currentImg, setCurrentImg] = useState(0);

  useEffect(() => {
    if (imageCount <= 1) return;
    const id = setInterval(() => {
      setCurrentImg((prev) => (prev + 1) % imageCount);
    }, 3000);
    return () => clearInterval(id);
  }, [imageCount]);

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev - 1 + imageCount) % imageCount);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev + 1) % imageCount);
  };

  const handleDotClick = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation();
    setCurrentImg(idx);
  };

  const tipo = TIPO_DISPLAY[property.type] || property.type;

  return (
    <div
      className="card h-100 border rounded-3 overflow-hidden shadow-sm bg-white"
      onClick={() => navigate(`/imovel/${property.code}`)}
      style={{ cursor: "pointer" }}
    >
      <div
        className="position-relative overflow-hidden"
        style={{ height: "210px" }}
      >
        <div
          style={{
            display: "flex",
            height: "100%",
            transform: `translateX(-${currentImg * 100}%)`,
            transition: "transform 0.5s ease-in-out",
          }}
        >
          {Array.from({ length: imageCount }, (_, idx) => (
            <img
              key={idx}
              src={property.images[idx]}
              alt={`${property.code} - ${idx + 1}`}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          ))}
        </div>

        {imageCount > 1 && (
          <>
            <button
              type="button"
              className="btn position-absolute top-50 start-0 translate-middle-y ms-1 p-0 d-flex align-items-center justify-content-center"
              style={{
                width: "28px",
                height: "28px",
                backgroundColor: "rgba(255,255,255,0.85)",
                borderRadius: "50%",
                opacity: 0,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              onClick={handlePrev}
            >
              <i
                className="fas fa-chevron-left"
                style={{ fontSize: "0.65rem", color: "#333" }}
              ></i>
            </button>
            <button
              type="button"
              className="btn position-absolute top-50 end-0 translate-middle-y me-1 p-0 d-flex align-items-center justify-content-center"
              style={{
                width: "28px",
                height: "28px",
                backgroundColor: "rgba(255,255,255,0.85)",
                borderRadius: "50%",
                opacity: 0,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
              onClick={handleNext}
            >
              <i
                className="fas fa-chevron-right"
                style={{ fontSize: "0.65rem", color: "#333" }}
              ></i>
            </button>
          </>
        )}

        <div className="position-absolute bottom-0 start-50 translate-middle-x mb-2 d-flex gap-1">
          {Array.from({ length: imageCount }, (_, idx) => (
            <span
              key={idx}
              className="rounded-circle d-inline-block"
              style={{
                width: "6px",
                height: "6px",
                backgroundColor:
                  idx === currentImg ? "#fff" : "rgba(255,255,255,0.45)",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onClick={(e) => handleDotClick(e, idx)}
            />
          ))}
        </div>
      </div>

      <div className="card-body p-4 d-flex flex-column justify-content-between">
        <div>
          <span className="text-muted text-uppercase small fw-semibold d-block mb-1">
            COD: {property.code}
          </span>
          <h6
            className="fw-bold mb-2"
            style={{ color: "#0B2545", minHeight: "44px", lineHeight: "1.4" }}
          >
            {tipo} - {property.location.neighborhood}
          </h6>
          <span className="text-muted small d-block mb-3">
            {property.location.neighborhood}, {property.location.city}
          </span>

          <div className="row g-2 py-3 border-top border-bottom my-3 row-cols-2">
            <div className="col d-flex align-items-center gap-2 text-muted small">
              <i className="fas fa-bed text-secondary opacity-75"></i>
              <span>
                <strong>{property.bedrooms}</strong> Quartos
              </span>
            </div>
            <div className="col d-flex align-items-center gap-2 text-muted small">
              <i className="fas fa-bath text-secondary opacity-75"></i>
              <span>
                <strong>{property.bathrooms}</strong> Banheiros
              </span>
            </div>
            <div className="col d-flex align-items-center gap-2 text-muted small">
              <i className="fas fa-car text-secondary opacity-75"></i>
              <span>
                <strong>{property.parkingSpots}</strong> Vagas
              </span>
            </div>
            <div className="col d-flex align-items-center gap-2 text-muted small">
              <i className="fas fa-ruler-combined text-secondary opacity-75"></i>
              <span>
                <strong>{property.builtArea ?? "-"}</strong> m²
              </span>
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between mt-2 pt-1">
          <div>
            <span
              className="text-muted d-block"
              style={{ fontSize: "0.65rem", fontWeight: 600 }}
            >
              VALOR
            </span>
            <h5 className="fw-bold m-0" style={{ color: "#0B2545" }}>
              {formatCurrency(property.price)}
            </h5>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyCard;
